import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, type Listing } from "../api.ts";
import { money } from "../format.ts";
import { pick, useI18n } from "../i18n.tsx";
import { IconArrow } from "../ui/Icons.tsx";
import { Pattern } from "../ui/Pattern.tsx";
import { Reveal } from "../ui/Reveal.tsx";

interface Vocab { cities: { key: string; name: { fr: string; ar?: string; en?: string } }[] }

export function HomePage() {
  const { t, locale } = useI18n();
  const [vocab, setVocab] = useState<Vocab | null>(null);
  const [f, setF] = useState({ cityKey: "", checkIn: "", checkOut: "", guests: "2" });
  const [items, setItems] = useState<Listing[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => { api<Vocab>("/vocab").then(setVocab).catch(() => undefined); }, []);

  const run = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    setFailed(false); setItems(null);
    const q = new URLSearchParams();
    if (f.cityKey) q.set("cityKey", f.cityKey);
    if (f.checkIn && f.checkOut) { q.set("checkIn", f.checkIn); q.set("checkOut", f.checkOut); }
    if (f.guests) q.set("guests", f.guests);
    try { setItems((await api<{ items: Listing[] }>(`/search?${q}`)).items); } catch { setFailed(true); setItems([]); }
  }, [f]);
  useEffect(() => { void run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- first load shows everything

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const withDates = f.checkIn && f.checkOut ? `?in=${f.checkIn}&out=${f.checkOut}&g=${f.guests}` : "";

  return (
    <>
      <section className="hero">
        <Pattern />
        <div className="wrap hero-inner">
          <span className="eyebrow">{t("hero.eyebrow")}</span>
          <h1 className="display">{t("tagline")}</h1>
          <p className="lead">{t("hero.lead")}</p>
          <form className="finder" onSubmit={run}>
            <label className="field wide"><span>{t("search.where")}</span>
              <select value={f.cityKey} onChange={set("cityKey")}>
                <option value="">{t("search.anywhere")}</option>
                {vocab?.cities.map((c) => <option key={c.key} value={c.key}>{pick(c.name, locale)}</option>)}
              </select>
            </label>
            <label className="field"><span>{t("search.checkIn")}</span><input type="date" value={f.checkIn} onChange={set("checkIn")} /></label>
            <label className="field"><span>{t("search.checkOut")}</span><input type="date" value={f.checkOut} min={f.checkIn} onChange={set("checkOut")} /></label>
            <label className="field wide"><span>{t("search.guests")}</span><input type="number" inputMode="numeric" min={1} value={f.guests} onChange={set("guests")} /></label>
            <button className="btn go" type="submit">{t("search.go")} <IconArrow /></button>
          </form>
        </div>
      </section>

      <section className="section wrap" aria-live="polite">
        <div className="section-head">
          <h2 className="h2"><span className="eyebrow"><span className="n num">01</span></span>{t("sel.title")}</h2>
          {items && items.length > 0 && <span className="eyebrow num">{String(items.length).padStart(2, "0")}</span>}
        </div>

        {items === null && <div className="grid">{[0, 1, 2].map((i) => <div key={i} className="skel" aria-hidden="true" />)}</div>}

        {failed && (
          <div className="note"><Pattern /><p className="h2">{t("error")}</p><button className="btn ghost" onClick={() => void run()}>{t("retry")}</button></div>
        )}
        {items?.length === 0 && !failed && (
          <div className="note"><Pattern /><p className="h2">{t("search.none")}</p><p className="muted small">{t("sel.empty.hint")}</p></div>
        )}

        {items && items.length > 0 && (
          <div className="grid">
            {items.map((l, i) => (
              <Reveal key={l.id} delay={(i % 3) * 90}>
                <Link to={`/l/${l.id}${withDates}`} className="card">
                  <div className="frame">
                    {l.photos[0] ? <img src={mediaUrl(l.photos[0])} alt="" loading="lazy" /> : <Pattern />}
                    <span className="idx num">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="card-body">
                    <span className="eyebrow">{vocab?.cities.find((c) => c.key === l.cityKey)?.name ? pick(vocab.cities.find((c) => c.key === l.cityKey)!.name, locale) : l.cityKey}</span>
                    <h3><span>{pick(l.title, locale)}</span></h3>
                    <div className="leader muted small">
                      <span className="num">{l.bedrooms} {t("listing.bedrooms")} · {l.maxGuests} {t("listing.guests")}</span>
                      <span className="price num" style={{ color: "var(--ink)" }}>{money(l.basePriceMinor, l.currency, locale)} <span className="muted">{t("perNight")}</span></span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="band section">
        <div className="wrap">
          <div className="section-head" style={{ borderColor: "var(--hairline)" }}><h2 className="h2"><span className="eyebrow"><span className="n num">02</span></span>{t("how.title")}</h2></div>
          <div className="steps">
            {[1, 2, 3].map((n) => (
              <Reveal key={n} delay={n * 90}>
                <div className="step"><span className="big num">0{n}</span><h3>{t(`how.${n}.t`)}</h3><p>{t(`how.${n}.d`)}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
